import { Observable, of, ReplaySubject, take } from 'rxjs';
import { SubjectHolder } from './subject-holder';
import { inject } from '@angular/core';
import { LocalStorageService } from './local-storage.service';
import { SolComponentsService } from './sol-components.service';
import { AuthenticationService } from './authentication.service';
import { StorageService } from './storage.service';
import { SessionStorageService } from './session-storage.service';
import { StateCalculators } from '../tools/state-tools';

export interface CacheObservableOptions<S> {
  calculator?: StateCalculators<S>;
  errorCalculator?: (error: any) => Partial<S>;
  forceRefresh?: boolean;
  cacheType?: ServiceCacheType;
  storageTimeToLiveSeconds?: number;
}

export interface CachedObservable<R> {
  response: R;
  version: string;
  timestamp: number;
  sessionId: string;
}

export enum ServiceCacheType {
  Memory,
  SessionStorage,
  LocalStorage,
}

export abstract class AbstractService<S> extends SubjectHolder<S> {
  private _cacheKeys: Record<string, boolean> = {};

  protected readonly sessionStorageService = inject(SessionStorageService);
  protected readonly localStorageService = inject(LocalStorageService);
  protected readonly solComponentsService = inject(SolComponentsService);
  protected readonly authenticationService = inject(AuthenticationService);

  protected constructor(initialState: S, calculators?: StateCalculators<S>) {
    super(initialState, calculators);
  }

  protected cacheObservable<R>(
    observable: Observable<R>,
    cacheKey: string,
    nextCalculator: (response: R, state: S) => Partial<S>,
    options?: CacheObservableOptions<S>,
  ): Observable<S> {
    // Cached locally?
    const forceRefresh = options?.forceRefresh || false;
    if (!forceRefresh && this._cacheKeys[cacheKey]) {
      return of(this.state);
    }

    // Cached in local storage?
    if (!forceRefresh && this.maybeReadFromStorage<R>(cacheKey, nextCalculator, options)) {
      return of(this.state);
    }

    // Execute and cache
    this._cacheKeys[cacheKey] = true;
    const subject = new ReplaySubject<S>(1);
    observable.pipe(take(1)).subscribe({
      next: response => {
        const partialState = nextCalculator(response, this.state);
        this.maybeWriteToStorage<R>(cacheKey, response, options);
        this.patch(partialState, options?.calculator);
        subject.next(this.state);
        subject.complete();
      },
      error: e => {
        console.error("Error while executing observable '" + cacheKey + "'", e);
        this._cacheKeys[cacheKey] = false;
        const errorCalculator = options?.errorCalculator;
        if (errorCalculator !== undefined) {
          this.patch(errorCalculator(e));
        }
        subject.error(e);
      },
    });
    return subject.asObservable();
  }

  private maybeReadFromStorage<R>(
    cacheKey: string,
    toPartialState: (response: R, state: S) => Partial<S>,
    options?: CacheObservableOptions<S>,
  ): boolean {
    // Configured?
    const cacheType = options?.cacheType || ServiceCacheType.Memory;
    if (!options || cacheType === ServiceCacheType.Memory) {
      return false;
    }

    // Cached?
    const storageService = this.getStorageService(cacheType);
    try {
      const ttl = options.storageTimeToLiveSeconds || 300;
      const cached = storageService.get<CachedObservable<R>>('service-cache.' + cacheKey);
      if (this.isCacheValid(cached, ttl)) {
        this.patch(toPartialState(cached!.response, this.state), options.calculator);
        this._cacheKeys[cacheKey] = true;
        return true;
      }
    } catch (e) {
      console.warn('Error while reading cached observable from local storage:', cacheKey);
      storageService.remove(cacheKey);
      return false;
    }

    // Expired
    storageService.remove(cacheKey);
    return false;
  }

  private isCacheValid<R>(cached: CachedObservable<R> | undefined, ttl: number): boolean {
    // Nothing cached
    if (cached === undefined || cached.response === undefined) {
      return false;
    }

    // Expired
    const now = Date.now();
    if (now > cached.timestamp + ttl * 1000) {
      return false;
    }

    // Session differs
    if (this.authenticationService.state.sessionId !== cached.sessionId) {
      return false;
    }

    // Version differs
    if (this.solComponentsService.version !== cached.version) {
      return false;
    }

    // All good
    return true;
  }

  private maybeWriteToStorage<R>(cacheKey: string, response: R, options?: CacheObservableOptions<S>): void {
    // Configured?
    const cacheType = options?.cacheType || ServiceCacheType.Memory;
    if (!options || cacheType === ServiceCacheType.Memory) {
      return;
    }

    // Cache
    const storageService = this.getStorageService(cacheType);
    const now = Date.now();
    const cached: CachedObservable<R> = {
      version: this.solComponentsService.version,
      sessionId: this.authenticationService.state.sessionId!,
      timestamp: now,
      response: response,
    };
    storageService.set<CachedObservable<R>>('service-cache.' + cacheKey, cached);
  }

  private getStorageService(cacheType: ServiceCacheType): StorageService {
    if (cacheType === ServiceCacheType.LocalStorage) {
      return this.localStorageService;
    } else if (cacheType === ServiceCacheType.SessionStorage) {
      return this.sessionStorageService;
    } else {
      throw new Error('Unsupported cache type: ' + cacheType);
    }
  }
}
